import { ReactNode, useState } from 'react';
import FluentSelect from '@/components/UI/FluentSelect';

export type SortMode = 'high' | 'low' | 'az' | 'za';

const SORT_OPTIONS = [
    { value: 'high' as const, label: 'High to low' },
    { value: 'low' as const, label: 'Low to high' },
    { value: 'az' as const, label: 'A → Z' },
    { value: 'za' as const, label: 'Z → A' },
];

interface Props {
    search: string;
    onSearch: (s: string) => void;
    sort: SortMode;
    onSort: (s: SortMode) => void;
    itemsCount: number;
    onRefresh: () => void;
    /** Extra slot on the right — game switcher + Deposit Crypto for deposit page */
    extras?: ReactNode;
    /** Mobile cart button — shown only when count > 0 (md and below). */
    cartCount?: number;
    onCartOpen?: () => void;
}

const Toolbar = ({
    search,
    onSearch,
    sort,
    onSort,
    itemsCount,
    onRefresh,
    extras,
    cartCount = 0,
    onCartOpen,
}: Props) => {
    const [spinning, setSpinning] = useState(false);

    return (
        <div className="surface flex items-center gap-3 flex-wrap py-3 px-4">
            <div className="flex items-center bg-layer_2 border border-stroke rounded-fl pl-4 pr-1 focus-within:border-accent_3 focus-within:bg-layer_3 focus-within:shadow-[0_0_22px_-8px_rgba(139,109,255,0.8)] transition-all flex-1 min-w-[140px] max-w-[280px] max-md:max-w-none">
                <input
                    value={search}
                    onChange={(e) => onSearch(e.target.value)}
                    placeholder="Search skins…"
                    className="font-chat-msg bg-transparent outline-none text-text_main py-2.5 px-2 text-sm w-full min-w-0 placeholder:text-text_4"
                />
                <button className="w-8 h-8 grid place-items-center text-text_3 hover:bg-layer_3 hover:text-text_main rounded-md transition-colors">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2]">
                        <circle cx="11" cy="11" r="7" />
                        <path d="m21 21-4.3-4.3" />
                    </svg>
                </button>
            </div>

            <FluentSelect<SortMode>
                value={sort}
                options={SORT_OPTIONS}
                onChange={onSort}
                minWidth={140}
                className="max-md:flex-1"
            />

            <span className="font-chat-nick inline-flex items-center gap-2 px-3.5 py-2 rounded-fl bg-layer_2 border border-stroke text-[11.5px] text-text_3 font-bold tracking-[0.04em] uppercase max-lg:hidden">
                Items
                <b className="text-accent_5 font-extrabold tabular-nums">{itemsCount}</b>
            </span>

            {extras && <>{extras}</>}

            {cartCount > 0 && onCartOpen && (
                <button
                    onClick={onCartOpen}
                    aria-label="Open cart"
                    className="md:hidden relative w-10 h-10 rounded-fl grid place-items-center text-white border border-transparent transition-transform hover:-translate-y-0.5 ml-auto"
                    style={{
                        background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                        boxShadow:
                            'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 18px -4px rgba(139,109,255,0.7)',
                    }}
                >
                    <svg
                        viewBox="0 0 24 24"
                        className="w-4 h-4 stroke-current fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
                    >
                        <circle cx="9" cy="20" r="1.5" />
                        <circle cx="18" cy="20" r="1.5" />
                        <path d="M3 4h2l2.6 12.5a2 2 0 0 0 2 1.5h7.5a2 2 0 0 0 2-1.6L21 7H6" />
                    </svg>
                    <span
                        className="font-chat-nick absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-extrabold text-[#5a3d00] tabular-nums"
                        style={{
                            background: 'linear-gradient(135deg, #ffe08a, #ffc24d)',
                            boxShadow: '0 0 10px -2px rgba(255,194,77,0.85)',
                        }}
                    >
                        {cartCount}
                    </span>
                </button>
            )}

            <button
                onClick={() => {
                    onRefresh();
                    setSpinning(true);
                    window.setTimeout(() => setSpinning(false), 600);
                }}
                aria-label="Refresh"
                className={`w-10 h-10 rounded-fl grid place-items-center text-text_2 bg-layer_2 border border-stroke hover:bg-layer_3 hover:text-accent hover:border-stroke_strong transition-colors ${cartCount > 0 && onCartOpen ? '' : 'ml-auto'}`}
            >
                <svg
                    viewBox="0 0 24 24"
                    className="w-4 h-4 stroke-current fill-none [stroke-width:2.2] [stroke-linecap:round] [stroke-linejoin:round]"
                    style={spinning ? { animation: 'rfSpin 0.6s linear' } : undefined}
                >
                    <path d="M21 12a9 9 0 1 1-3.5-7.1" />
                    <path d="M21 4v5h-5" />
                </svg>
            </button>
        </div>
    );
};

export default Toolbar;
